import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { ApiResponse, PageableApiResponse } from '../../api-response';
import {
  CreateDirectoryRequest,
  FileItem,
  MoveItemRequest,
  RenameItemRequest,
} from '../models/file-manager.model';
import { BaseApiService } from '../../../core/base-api.service';

@Injectable({
  providedIn: 'root'
})
export class FileManagerService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${this.coreApiUrl}/api/v1/files`;

  uploadFile(file: File, parentId?: string | null, name?: string, description?: string): Observable<ApiResponse<FileItem>> {
    const formData = new FormData();
    formData.append('file', file);
    if (parentId != null) formData.append('parentId', parentId.toString());
    if (name) formData.append('name', name);
    if (description) formData.append('description', description);
    return this.http.post<ApiResponse<FileItem>>(`${this.baseUrl}/upload`, formData);
  }

  createDirectory(request: CreateDirectoryRequest): Observable<ApiResponse<FileItem>> {
    return this.http.post<ApiResponse<FileItem>>(`${this.baseUrl}/organization/directories`, request);
  }

  getRootContents(page = 0, size = 20): Observable<PageableApiResponse<FileItem>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageableApiResponse<FileItem>>(`${this.baseUrl}/organization/contents`, { params });
  }

  getDirectoryContents(id: string, page = 0, size = 20): Observable<PageableApiResponse<FileItem>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<PageableApiResponse<FileItem>>(`${this.baseUrl}/organization/directories/${id}/contents`, { params });
  }

  getItem(id: string): Observable<ApiResponse<FileItem>> {
    return this.http.get<ApiResponse<FileItem>>(`${this.baseUrl}/organization/items/${id}`);
  }

  renameItem(id: string, request: RenameItemRequest): Observable<ApiResponse<FileItem>> {
    return this.http.patch<ApiResponse<FileItem>>(`${this.baseUrl}/organization/items/${id}/rename`, request);
  }

  moveItem(id: string, request: MoveItemRequest): Observable<ApiResponse<FileItem>> {
    return this.http.patch<ApiResponse<FileItem>>(`${this.baseUrl}/organization/items/${id}/move`, request);
  }

  deleteItem(id: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(`${this.baseUrl}/organization/items/${id}`);
  }

  downloadFile(id: string): Observable<Blob> {
    return this.http.get<{ url: string }>(`${this.baseUrl}/download/${id}`).pipe(
      switchMap(({ url }) => this.http.get(url, { responseType: 'blob' }))
    );
  }

  downloadThumbnail(id: string): Observable<Blob> {
    return this.http.get<{ url: string }>(`${this.baseUrl}/download/${id}/thumbnail`).pipe(
      switchMap(({ url }) => this.http.get(url, { responseType: 'blob' }))
    );
  }

  getFileUrl(id: string): string {
    return `${this.baseUrl}/download/${id}`;
  }

  getFileSasUrl(id: string): Observable<string> {
    return this.http.get<{ url: string }>(`${this.baseUrl}/download/${id}`).pipe(
      switchMap(({ url }) => [url])
    );
  }
}
